import { useEffect, useId, useState } from 'react';
import { RetryableError } from '../../../shared/RetryableError';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { isPayBasis, payBasisLabels, setupReviewSeverityLabels, type CompensationSetupReview, type SetupReviewItem } from '../types';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

type Outcome = { attempt: number; review: CompensationSetupReview; error: null } | { attempt: number; review: null; error: string };

function Item({ item }: { item: SetupReviewItem }) {
  const more = item.count !== null && item.count > item.names.length ? item.count - item.names.length : 0;
  return (
    <li className={`hdc-review__item hdc-review__item--${item.severity.toLowerCase()}`} data-severity={item.severity} data-testid={`setup-review-item-${item.key}`}>
      <p>
        <span className="hdc-badge">{setupReviewSeverityLabels[item.severity]}</span> <strong>{item.label}</strong>
      </p>
      <p>{item.message}</p>
      {item.names.length > 0 && (
        <ul className="hdc-review__names">
          {item.names.map((n) => <li key={n}>{n}</li>)}
          {more > 0 && <li className="muted">and {more} more</li>}
        </ul>
      )}
    </li>
  );
}

/** Prints only the review: the class that shows nothing else is on <body> only while the print call runs, and is always removed (also when printing ends later or throws). */
function printReview() {
  const body = document.body;
  const clear = () => { body.classList.remove(PRINTING_CLASS); window.removeEventListener('afterprint', clear); };
  body.classList.add(PRINTING_CLASS);
  window.addEventListener('afterprint', clear);
  try {
    window.print();
  } finally {
    clear();
  }
}

const PRINTING_CLASS = 'hdc-printing-review';

function ReviewBody({ review, onClose }: { review: CompensationSetupReview; onClose: () => void }) {
  const uid = useId();
  const { workTypes, companyRates, summary } = review;
  return (
    <div className="hdc-review" data-testid="setup-review" role="region" aria-labelledby={`${uid}-title`}>
      <h4 id={`${uid}-title`}>Your compensation setup</h4>
      <p className="hdc-note">Counted on {review.asOf}. This review only reads: it changed nothing, and nothing in it needs to be done for payroll to keep working exactly as it does today.</p>

      <p data-testid="setup-review-summary">
        <strong>{summary.review}</strong> to look at · <strong>{summary.info}</strong> good to know · <strong>{summary.ok}</strong> fine
      </p>
      <ul className="hdc-review__items" aria-label="What the review found">
        {review.items.map((i) => <Item key={i.key} item={i} />)}
      </ul>

      <h5>At a glance</h5>
      <dl className="hdc-review__counts" data-testid="setup-review-counts">
        <div><dt>EPN default work types</dt><dd>{workTypes.systemActive} active{workTypes.systemInactive > 0 ? `, ${workTypes.systemInactive} retired` : ''}</dd></div>
        <div><dt>Your own work types</dt><dd>{workTypes.companyActive} active{workTypes.companyInactive > 0 ? `, ${workTypes.companyInactive} inactive` : ''}</dd></div>
        <div>
          <dt>Company rates in effect</dt>
          <dd>
            {companyRates.active}
            {companyRates.byPayBasis.length > 0 && (
              <> ({companyRates.byPayBasis.map((b) => `${isPayBasis(b.payBasis) ? payBasisLabels[b.payBasis] : b.payBasis}: ${b.count}`).join(', ')})</>
            )}
          </dd>
        </div>
      </dl>

      <h5>What this review does not cover</h5>
      <ul className="hdc-review__notes" data-testid="setup-review-not-included">
        {review.notIncluded.map((n) => <li key={n.area}><strong>{n.label}.</strong> {n.whereToLook}</li>)}
      </ul>

      <div className="jr-actions hdc-review__actions no-print">
        <button type="button" className="jr-btn jr-btn--secondary" onClick={printReview}>Print this review</button>
        <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose}>Close the review</button>
      </div>
    </div>
  );
}

/**
 * "Compensation setup review": a read-only, printable look at how the company's setup reads under the universal model: where the industry answer comes from, the company's
 * own work types and the company rates in effect, a few things worth a look, and what the review does not cover. It is only read when the person asks for it. It changes
 * nothing, rewrites nothing and nothing in it blocks payroll. Healthcare-era words and data are never migrated; saved payroll keeps the words it was saved with.
 */
export function CompensationSetupReviewPanel() {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    healthcareRatesGateway
      .getCompensationSetupReview(controller.signal)
      .then((review) => { if (!controller.signal.aborted) setOutcome({ attempt, review, error: null }); })
      .catch((err: unknown) => { if (!controller.signal.aborted) setOutcome({ attempt, review: null, error: healthcareErrorMessage(err, 'The setup review could not be loaded.') }); });
    return () => controller.abort();
  }, [open, attempt]);

  const current = open && outcome?.attempt === attempt ? outcome : null;

  return (
    <section className="hdc-section hdc-review-section" aria-labelledby={`${uid}-title`}>
      <h3 id={`${uid}-title`}>Compensation setup review</h3>
      <p className="jr-hint">
        A read-only look at how your setup reads now that EPN serves every industry, and anything you may want to check. It changes nothing and rewrites nothing, and you can print it.
      </p>
      {!open && (
        <p>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { setOutcome(null); setAttempt((n) => n + 1); setOpen(true); }}>Review my setup</button>
        </p>
      )}
      {open && current === null && <p className="muted" role="status">Reading your setup…</p>}
      {open && current?.error && <RetryableError message={current.error} onRetry={() => { setOutcome(null); setAttempt((n) => n + 1); }} />}
      {open && current?.review && <ReviewBody review={current.review} onClose={() => setOpen(false)} />}
    </section>
  );
}
